import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Loader2, Lock, LogIn } from "lucide-react";

export default function CrmLogin() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // Already signed in → send to dashboard.
  useEffect(() => {
    if (user) window.location.replace("/crm");
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back.");
      window.location.replace("/crm");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/crm";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center px-6 relative">
      <div className="ambient-glow" />
      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="relative w-full max-w-md rounded-3xl border border-line bg-white/[0.07] p-8 backdrop-blur-xl shadow-2xl shadow-black/40" data-testid="crm-login-card">
        <div className="inline-flex items-center gap-2 rounded-full border border-line bg-white/5 px-3 py-1 text-[11px] uppercase tracking-widest text-brand">
          <Lock size={12} /> Consultant access
        </div>
        <h1 className="font-serif text-3xl text-forest mt-4">PropIntellix CRM</h1>
        <p className="text-mutedText text-sm mt-1.5">Sign in to view your clients and download their detail dossiers.</p>

        <form onSubmit={submit} className="mt-7 space-y-3">
          <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="w-full rounded-xl border border-line bg-white/10 px-4 py-3 text-forest placeholder:text-mutedText/80 focus:outline-none focus:ring-2 focus:ring-brand" />
          <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full rounded-xl border border-line bg-white/10 px-4 py-3 text-forest placeholder:text-mutedText/80 focus:outline-none focus:ring-2 focus:ring-brand" />
          <button data-testid="login-submit" type="submit" disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand px-6 py-3.5 text-white text-sm font-medium shadow-xl shadow-brand/25 transition hover:bg-brand-dark disabled:opacity-50">
            {busy ? <><Loader2 size={16} className="animate-spin" /> Signing in…</> : <><LogIn size={16} /> Sign in</>}
          </button>
        </form>

        <div className="my-5 flex items-center gap-3 text-mutedText/60 text-xs">
          <div className="h-px flex-1 bg-line" /> or <div className="h-px flex-1 bg-line" />
        </div>

        <button data-testid="login-google" onClick={googleSignIn}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-line bg-white/5 px-6 py-3.5 text-forest text-sm font-medium transition hover:bg-white/10">
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" />
          Continue with Google
        </button>
        <p className="text-mutedText/60 text-xs mt-4 text-center">Approved consultants only.</p>
      </motion.div>
    </div>
  );
}
